// src/features/dashboard/components/EmployeeLoggedHoursReport/SessionLog.tsx

import {
  HourlyLoggedResponse,
  LoggedHoursKpiResponse,
  SessionLogResponse,
} from '../../types/Employee/employeeLoggedHours.types';

interface Props {
  sessions?: SessionLogResponse[];
  data?: HourlyLoggedResponse | null;
  kpi?: LoggedHoursKpiResponse | null;
}

/* ============================================================
 * COMPONENT
 * ============================================================ */

const SessionLog = ({
  sessions = [],
  data,
  kpi,
}: Props) => {
  /*
   * ==========================================================
   * API VALUES
   *
   * Do NOT calculate these values here.
   *
   * Active / Meeting / Idle
   *     -> hourlyLogged.summary
   *
   * Logged Hours
   *     -> KPI loggedToday
   * ==========================================================
   */

  const activeTotal =
    data?.summary?.activeTotal ?? '--';

  const meetingTotal =
    data?.summary?.meetingTotal ?? '--';

  const idleTotal =
    data?.summary?.idleTotal ?? '--';

  /*
   * Logged Hours MUST come directly from KPI.
   *
   * Example:
   * kpi.loggedToday = "22h 12m"
   *
   * Do not calculate:
   * Active + Meeting + Idle
   */

  const loggedHours =
    kpi?.loggedToday ?? '--';

  /*
   * sessions is intentionally not used for calculations.
   *
   * It is kept in Props because the parent already provides
   * session data and the component interface may be used
   * elsewhere.
   */
  void sessions;

  return (
    <div
      className="
        w-full
        min-w-0
        rounded-[16px]
        border
        border-[#D0D5DD]
        bg-white
        p-3
      "
    >
      {/* ======================================================
          HEADER
      ====================================================== */}

      <div
        className="
          mb-3
          flex
          min-w-0
          flex-wrap
          items-center
          gap-2
        "
      >
        <h3
          className="
            shrink-0
            text-[14px]
            font-semibold
            leading-[20px]
            text-[#002B5B]
          "
        >
          Session Log
        </h3>

        <span
          className="
            rounded-full
            bg-[#EEF4FF]
            px-2
            py-[3px]
            text-[9px]
            font-medium
            leading-[14px]
            text-[#2563EB]
          "
        >
          Today&apos;s activity blocks
        </span>
      </div>

      {/* ======================================================
          TITLE
      ====================================================== */}

      {/* <div
        className="
          mb-2
          text-[12px]
          font-semibold
          leading-[18px]
          text-[#111827]
        "
      >
        Today&apos;s Hour-by-Hour Log
      </div> */}

      {/* ======================================================
          ACTIVITY SUMMARY
      ====================================================== */}

      <div
        className="
          w-full
          overflow-hidden
          rounded-[8px]
          border
          border-[#EAECF0]
          bg-white
        "
      >
        {/* ====================================================
            ACTIVE
        ==================================================== */}

        <div
          className="
            flex
            min-h-[42px]
            w-full
            items-center
            justify-between
            gap-3
            border-b
            border-[#EAECF0]
            px-3
          "
        >
          <div
            className="
              flex
              min-w-0
              items-center
              gap-2
            "
          >
            <span
              className="
                h-[10px]
                w-[10px]
                shrink-0
                rounded-[3px]
                bg-[#4CC66F]
              "
            />

            <span
              className="
                text-[12px]
                font-medium
                text-[#344054]
              "
            >
              Active
            </span>
          </div>

          <span
            className="
              shrink-0
              text-[12px]
              font-semibold
              text-[#667085]
            "
          >
            {activeTotal}
          </span>
        </div>

        {/* ====================================================
            MEETING
        ==================================================== */}

        <div
          className="
            flex
            min-h-[42px]
            w-full
            items-center
            justify-between
            gap-3
            border-b
            border-[#EAECF0]
            px-3
          "
        >
          <div
            className="
              flex
              min-w-0
              items-center
              gap-2
            "
          >
            <span
              className="
                h-[10px]
                w-[10px]
                shrink-0
                rounded-[3px]
                bg-[#5A8DEE]
              "
            />

            <span
              className="
                text-[12px]
                font-medium
                text-[#344054]
              "
            >
              Meeting
            </span>
          </div>

          <span
            className="
              shrink-0
              text-[12px]
              font-semibold
              text-[#667085]
            "
          >
            {meetingTotal}
          </span>
        </div>

        {/* ====================================================
            IDLE
        ==================================================== */}

        <div
          className="
            flex
            min-h-[42px]
            w-full
            items-center
            justify-between
            gap-3
            px-3
          "
        >
          <div
            className="
              flex
              min-w-0
              items-center
              gap-2
            "
          >
            <span
              className="
                h-[10px]
                w-[10px]
                shrink-0
                rounded-[3px]
                bg-[#F3AD2B]
              "
            />

            <span
              className="
                text-[12px]
                font-medium
                text-[#344054]
              "
            >
              Idle
            </span>
          </div>

          <span
            className="
              shrink-0
              text-[12px]
              font-semibold
              text-[#667085]
            "
          >
            {idleTotal}
          </span>
        </div>
      </div>

      {/* ======================================================
          LOGGED HOURS
      ====================================================== */}

      <div
        className="
          mt-3
          flex
          items-center
          justify-between
          gap-3
          border-t
          border-[#D0D5DD]
          pt-3
        "
      >
        <span
          className="
            text-[13px]
            font-semibold
            text-[#111827]
          "
        >
          Total Hours
        </span>

        <span
          className="
            shrink-0
            text-[16px]
            font-bold
            text-[#111827]
          "
        >
          {loggedHours}
        </span>
      </div>
    </div>
  );
};

export default SessionLog;